import { defineComponent as _defineComponent } from 'vue'
import { computed, PropType, reactive, ref, watch } from 'vue';
import { AmountType, PaginatedResponse, PremiumPaymentType, VanillaPriceResponse } from 'ah-api-gateways';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import FormattedNumber from 'ah-common-lib/src/number/FormattedNumber.vue';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import CountdownTimer from 'ah-common-lib/src/common/components/CountdownTimer.vue';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { useAuthStore } from '@/app/store/authStore';
import { getServices } from '@/app/services';
import PaymentScheduleListing, { OptionPaymentSchedule } from './PaymentScheduleListing.vue';
import { paymentScheduleTableFields } from './tableFields/paymentScheduleTableFields';
import { addDays, addMonths, addQuarters, format } from 'date-fns';
import { PaymentFormDetails, PaymentScheduleFrequency } from '@/app/models/payments';
import { makeFormModel } from 'ah-common-lib/src/form/helpers';
import { FormDefinition } from 'ah-common-lib/src/form/interfaces';
import { checkboxField } from 'ah-common-lib/src/form/models';


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentScheduleReview',
  props: {
  solution: {
    type: Object as PropType<VanillaPriceResponse>,
    required: true,
  },
  model: {
    type: Object as PropType<PaymentFormDetails>,
    required: true,
  },
  disabledDates: {
    type: Array as PropType<String[]>,
    required: true,
  },
  isBooking: {
    type: Boolean,
    default: false,
  },
},
  emits: {
  'update:solution': (_solution: VanillaPriceResponse) => true,
  'update:isLoading': (_isLoading: boolean) => true,
  'update:isInvalid': (_isInvalid: boolean) => true,
  back: () => true,
},
  setup(__props, { emit }) {

const props = __props;





const TERMSHEET_FILE_URL = 'https://www.alt21.com/wp-content/uploads/2021/06/HG-Termsheet-v1.pdf';

const formatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2, minimumFractionDigits: 2 });

const requestManager = useRequestManager().manager;

const onBehalfOfClient = useOnBehalfOf();

const services = getServices();

const authStore = useAuthStore();

const clientId = computed(() => authStore.loggedInIdentity?.client?.id ?? onBehalfOfClient.value?.id);

const isRequestingNewPrice = ref<boolean>(false);

const pause = computed(() => isRequestingNewPrice.value || props.isBooking);

const sellCurrency = computed(() =>
  props.solution.ccy1.amountType === AmountType.SELL ? props.solution.ccy1.currency : props.solution.ccy2.currency
);

const buyCurrency = computed(() =>
  props.solution.ccy1.amountType === AmountType.BUY ? props.solution.ccy1.currency : props.solution.ccy2.currency
);

const totalPremium = computed(() => {
  if (props.solution.ccy1.amountType === AmountType.SELL) {
    const premiumOption = props.solution.ccy1.optionsCurrencyData.paymentOptions.find(
      (paymentOption) => paymentOption.premiumPaymentType === PremiumPaymentType.UPFRONT
    );

    return premiumOption?.totalPremium;
  }

  const premiumOption = props.solution.ccy2.optionsCurrencyData.paymentOptions.find(
    (paymentOption) => paymentOption.premiumPaymentType === PremiumPaymentType.UPFRONT
  );

  return premiumOption?.totalPremium;
});

const sellAmount = computed(() => {
  if (props.solution.ccy1.amountType === AmountType.SELL) {
    if (totalPremium.value) {
      return props.solution.ccy1.clientAmount + totalPremium.value;
    }
  } else {
    if (totalPremium.value) {
      return props.solution.ccy2.clientAmount + totalPremium.value;
    }
  }

  return undefined;
});

const buyAmount = computed(() =>
  props.solution.ccy1.amountType === AmountType.BUY
    ? props.solution.ccy1.clientAmount
    : props.solution.ccy2.clientAmount
);

const buyPerFrequency = computed(() => buyAmount.value / props.model.mvpNumPayments);

const sellPerFrequency = computed(() => (sellAmount.value ? sellAmount.value / props.model.mvpNumPayments : undefined));

const paymentsFrequency = computed(() =>
  props.model.mvpFrequency === PaymentScheduleFrequency.MONTHLY ? 'month' : 'quarter'
);

function createVanillaOption(solution: VanillaPriceResponse) {
  const isPrimaryRate = solution.ccy1.isPrimaryRate === true ? solution.ccy1 : solution.ccy2;
  const isFixedSide = solution.ccy1.isFixedSide === true ? solution.ccy1 : solution.ccy2;
  const sellCurrency = solution.ccy1.amountType === AmountType.SELL ? solution.ccy1.currency : solution.ccy2.currency;
  const buyCurrency = solution.ccy1.amountType === AmountType.BUY ? solution.ccy1.currency : solution.ccy2.currency;

  return {
    sellCurrency: sellCurrency,
    buyCurrency: buyCurrency,
    amount: isFixedSide.clientAmount,
    amountType: isFixedSide.amountType,
    settlementDate: formatDate(new Date(solution.settlementDate), 'yyyy-MM-dd'),
    strikeRate: isPrimaryRate.optionsCurrencyData.selectedStrikeRate,
    clientId: clientId.value,
  };
}

function refreshSolution() {
  isRequestingNewPrice.value = true;
  return requestManager
    .sameOrCancelAndNew(
      'createVanillaOptions',
      services.pricingEngine.createVanillaOptionsQuote(createVanillaOption(props.solution), onBehalfOfClient.value?.id)
    )
    .subscribe((price) => {
      isRequestingNewPrice.value = false;
      emit('update:solution', price);
    });
}

const expirationTime = computed(() => new Date(props.solution.priceExpirationTimestamp));

const config = {
  tableFields: paymentScheduleTableFields,
};

const paymentsSchedule = computed(() => {
  let paymentDate = new Date(props.model.mvpFirstPaymentDate);
  let payments = [
    {
      date: props.model.mvpFirstPaymentDate,
      sellAmount: sellPerFrequency.value,
      buyAmount: buyPerFrequency.value,
      sellCurrency: sellCurrency.value,
      buyCurrency: buyCurrency.value,
    } as OptionPaymentSchedule,
  ];

  for (let i = 1; i < props.model.mvpNumPayments; i++) {
    if (props.model.mvpFrequency === PaymentScheduleFrequency.MONTHLY) {
      paymentDate = addMonths(new Date(props.model.mvpFirstPaymentDate), i);
    } else {
      paymentDate = addQuarters(new Date(props.model.mvpFirstPaymentDate), i);
    }

    while (props.disabledDates.includes(format(paymentDate, 'dd-MM-yyyy'))) {
      paymentDate = addDays(paymentDate, 1);
    }

    payments.push({
      date: paymentDate.toISOString(),
      sellAmount: sellPerFrequency.value ?? 0,
      buyAmount: buyPerFrequency.value,
      sellCurrency: sellCurrency.value,
      buyCurrency: buyCurrency.value,
    });
  }

  return {
    list: payments,
    pageNumber: 0,
    pageSize: payments.length,
    total: payments.length,
  } as PaginatedResponse<OptionPaymentSchedule>;
});

watch(
  () => requestManager.anyPending,
  () => {
    emit('update:isLoading', requestManager.anyPending);
  }
);

const agreementFM = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'agreementConfirm',
    fieldType: 'form',
    fields: [
      checkboxField('termsheetAgreed', '', false, {
        required: true,
        errorMessages: {
          mustAccept: 'You must accept the Termsheet Agreement to proceed.',
        },
      }),
    ],
  }),
  validation: null,
});

const isInvalid = computed(() => !!agreementFM.validation?.$invalid);

watch(
  isInvalid,
  () => {
    emit('update:isInvalid', isInvalid.value);
  },
  { immediate: true }
);

function openTermsheetFile() {
  window.open(TERMSHEET_FILE_URL, '_blank');
}

return { __sfc: true,props, emit, TERMSHEET_FILE_URL, formatter, requestManager, onBehalfOfClient, services, authStore, clientId, isRequestingNewPrice, pause, sellCurrency, buyCurrency, totalPremium, sellAmount, buyAmount, buyPerFrequency, sellPerFrequency, paymentsFrequency, createVanillaOption, refreshSolution, expirationTime, config, paymentsSchedule, agreementFM, isInvalid, openTermsheetFile, FormattedNumber, formatCurrencyValue, CountdownTimer, PaymentScheduleListing }
}

})